/**
 * Owner: Masa Bayu
 * File: demo-author-sales.ts
 * Date:Time: 2026-09-16 14:41
 * Description: Demo confirmed orders → SaleRecord projection for T6-E4 UI.
 */

import {
  confirmOrderAfterSuccessfulPayment,
  createAuthorCapableAccount,
  createPendingOrderDraft,
  createPaymentAttempt,
  createPaymentIntent,
  createQxreadAuthorAccount,
  createQxreadSession,
  projectAuthorSalesSummary,
  projectSaleRecordsFromConfirmedOrders,
  type QxreadAuthorSalesSummary,
  type QxreadSaleRecord,
  type QxreadCheckoutSession,
} from '@qx/qxread-core';

const DEMO_ACCOUNT_ID = 'qxrdacc_demo_author';
const DEMO_NOW = '2026-09-16T06:41:00.000Z';
const DEMO_PUB = 'qxrdpub_demo_store';

function demoCheckout(): QxreadCheckoutSession {
  return Object.freeze({
    checkoutId: 'qxrdchk_demo_sales',
    cartId: 'qxrdcart_demo_sales',
    sessionKey: 'qxrdsess_demo_sales',
    state: 'ready' as const,
    totals: Object.freeze({
      currency: 'USD',
      sellerSubtotalMinorUnits: 1299,
      serviceFeeMinorUnits: 58,
      buyerTotalMinorUnits: 1357,
      affiliateContractMinorUnits: 65,
      referralContractMinorUnits: 65,
      rbsContractMinorUnits: 0,
      uniquePublicationCount: 1,
      ledgerLines: Object.freeze([]),
      attributions: Object.freeze([]),
    }),
    blockers: Object.freeze([] as string[]),
    createdAt: DEMO_NOW,
  });
}

let demoAccount = createAuthorCapableAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
});
let demoSession = createQxreadSession({
  state: 'authenticated',
  accountId: DEMO_ACCOUNT_ID,
  issuedAt: DEMO_NOW,
});
let demoAuthor = createQxreadAuthorAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
  slug: 'demo-author',
  createdAt: DEMO_NOW,
  updatedAt: DEMO_NOW,
});
let demoRecords: readonly QxreadSaleRecord[] = Object.freeze([]);
let demoSummary: QxreadAuthorSalesSummary | null = null;

function ensureActors(): void {
  demoAccount = createAuthorCapableAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo Author',
  });
  demoSession = createQxreadSession({
    state: 'authenticated',
    accountId: DEMO_ACCOUNT_ID,
    issuedAt: DEMO_NOW,
  });
  demoAuthor = createQxreadAuthorAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo Author',
    slug: 'demo-author',
    createdAt: DEMO_NOW,
    updatedAt: DEMO_NOW,
  });
}

export function resetDemoAuthorSales(): void {
  ensureActors();
  demoRecords = Object.freeze([]);
  demoSummary = null;
}

export function getDemoSaleRecords(): readonly QxreadSaleRecord[] {
  return demoRecords;
}

export function getDemoSalesSummary(): QxreadAuthorSalesSummary | null {
  return demoSummary;
}

export function projectDemoAuthorSales(): string {
  ensureActors();
  const pending = createPendingOrderDraft(demoCheckout(), [
    {
      publicationId: DEMO_PUB,
      title: 'River Atlas',
      quantity: 1,
      sellerPriceMinorUnits: 1299,
      currency: 'USD',
    },
  ]);
  const intent = createPaymentIntent(pending, { idempotencyKey: 'demo_sales_pay' });
  const attempt = createPaymentAttempt(intent);
  const succeeded = Object.freeze({
    ...attempt,
    state: 'succeeded' as const,
    updatedAt: DEMO_NOW,
  });
  const confirmed = confirmOrderAfterSuccessfulPayment(pending, succeeded, {
    confirmedAt: DEMO_NOW,
  });
  demoRecords = projectSaleRecordsFromConfirmedOrders({
    authorAccount: demoAuthor,
    authorOwnedPublicationIds: new Set([DEMO_PUB]),
    confirmedOrders: [confirmed],
    recordedAt: DEMO_NOW,
  });
  demoSummary = projectAuthorSalesSummary({
    session: demoSession,
    account: demoAccount,
    authorAccount: demoAuthor,
    saleRecords: demoRecords,
    projectedAt: DEMO_NOW,
  });
  return `Sales projected · count=${String(demoSummary.saleCount)} · subtotal=${String(demoSummary.sellerSubtotalMinorUnits)} ${demoSummary.currency} · inventedByAuthorUi=false`;
}
